import { useEffect, useRef, useState } from 'react';
import { api, type ProductInput } from '../../lib/api';
import { euro } from '../../lib/format';
import { fileToResizedDataURL } from '../../lib/image';
import { useToasts } from '../../lib/toast';
import { OptionsEditor } from './OptionsEditor';
import { MenuImport } from './MenuImport';
import type { CategoryDto, ProductDto } from '@taj/types';

const ALLERGENS = ['Gluten', 'Crustacés', 'Œufs', 'Poisson', 'Arachides', 'Soja', 'Lait',
  'Fruits à coque', 'Céleri', 'Moutarde', 'Sésame', 'Sulfites', 'Lupin', 'Mollusques'];

interface FormState {
  id?: string;
  name: string;
  nameEn: string;
  descriptionEn: string;
  categoryId: string;
  priceEuro: string;
  emoji: string;
  description: string;
  badges: string;
  allergens: string[];
  available: boolean;
  trackStock: boolean;
  stockQty: string;
  threshold: string;
  photoUrl: string;
}
const empty = (categoryId: string): FormState => ({
  name: '', nameEn: '', descriptionEn: '', categoryId, priceEuro: '', emoji: '🍽️', description: '', badges: '', allergens: [],
  available: true, trackStock: false, stockQty: '0', threshold: '5', photoUrl: '',
});

export function CarteManager({ onBack, openCats = false }: { onBack: () => void; openCats?: boolean }) {
  const push = useToasts((s) => s.push);
  const [menu, setMenu] = useState<CategoryDto[]>([]);
  const [cats, setCats] = useState<{ id: string; name: string; nameEn: string | null; emoji: string | null; imageUrl: string | null }[]>([]);
  const catFileRef = useRef<HTMLInputElement>(null);
  const catTargetRef = useRef<{ id: string; name: string } | null>(null);
  const prodFileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [showCats, setShowCats] = useState(openCats);
  const [importing, setImporting] = useState(false);
  const [optionsFor, setOptionsFor] = useState<ProductDto | null>(null);

  const load = () => {
    api.getMenu().then(setMenu).catch((e) => push(e.message, 'warn'));
    api.listCategories().then(setCats).catch((e) => push(e.message, 'warn'));
  };
  useEffect(load, []);

  const openNew = () => setForm(empty(cats[0]?.id ?? ''));
  const openEdit = (p: ProductDto) =>
    setForm({
      id: p.id,
      name: p.name,
      nameEn: p.nameEn ?? '',
      descriptionEn: p.descriptionEn ?? '',
      categoryId: p.categoryId,
      priceEuro: (p.priceCents / 100).toFixed(2),
      emoji: p.emoji ?? '🍽️',
      description: p.description ?? '',
      badges: p.badges.join(', '),
      allergens: p.allergens,
      available: p.available,
      trackStock: p.stock?.trackStock ?? false,
      stockQty: String(p.stock?.quantity ?? 0),
      threshold: String(p.stock?.threshold ?? 5),
      photoUrl: p.photoUrl ?? '',
    });

  const toggleAllergen = (a: string) =>
    setForm((f) => (f ? { ...f, allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a] } : f));

  const save = async () => {
    if (!form) return;
    const cents = Math.round(parseFloat(form.priceEuro.replace(',', '.')) * 100);
    if (!form.name.trim()) return push('Le nom est obligatoire', 'warn');
    if (!form.categoryId) return push('Choisissez une catégorie', 'warn');
    if (!Number.isFinite(cents) || cents < 0) return push('Prix invalide', 'warn');
    const payload: ProductInput = {
      name: form.name.trim(),
      nameEn: form.nameEn.trim(),
      description: form.description.trim(),
      descriptionEn: form.descriptionEn.trim(),
      categoryId: form.categoryId,
      priceCents: cents,
      emoji: form.emoji.trim() || '🍽️',
      available: form.available,
      badges: form.badges.split(',').map((b) => b.trim()).filter(Boolean),
      allergens: form.allergens,
      trackStock: form.trackStock,
      stockQty: parseInt(form.stockQty, 10) || 0,
      threshold: parseInt(form.threshold, 10) || 0,
      photoUrl: form.photoUrl,
    };
    setSaving(true);
    try {
      if (form.id) await api.updateProduct(form.id, payload);
      else await api.createProduct(payload);
      push(form.id ? '✅ Plat modifié' : '✅ Plat ajouté');
      setForm(null);
      load();
    } catch (e: any) {
      push(e.message, 'warn');
    } finally {
      setSaving(false);
    }
  };

  const toggleAvail = async (p: ProductDto) => {
    try {
      await api.updateProduct(p.id, { available: !p.available });
      load();
    } catch (e: any) {
      push(e.message, 'warn');
    }
  };

  const addCat = async () => {
    const name = prompt('Nom de la nouvelle catégorie (FR) ?');
    if (!name?.trim()) return;
    const nameEn = prompt('🇬🇧 Nom anglais (EN) ? (optionnel)', '') || undefined;
    const emoji = prompt('Emoji ? (ex : 🍕)', '🍽️') || '🍽️';
    try { await api.createCategory({ name: name.trim(), nameEn, emoji }); push('✅ Catégorie ajoutée'); load(); }
    catch (e: any) { push(e.message, 'warn'); }
  };
  const renameCat = async (c: { id: string; name: string; nameEn?: string | null; emoji: string | null }) => {
    const name = prompt('Nom de la catégorie (FR)', c.name);
    if (!name?.trim()) return;
    const nameEn = prompt('🇬🇧 Nom anglais (EN)', c.nameEn ?? '') || undefined;
    const emoji = prompt('Emoji', c.emoji ?? '🍽️') || c.emoji || '🍽️';
    try { await api.updateCategory(c.id, { name: name.trim(), nameEn, emoji }); push('✅ Catégorie modifiée'); load(); }
    catch (e: any) { push(e.message, 'warn'); }
  };
  const delCat = async (c: { id: string; name: string }) => {
    if (!confirm(`Supprimer la catégorie « ${c.name} » ?`)) return;
    try { await api.deleteCategory(c.id); push('🗑 Catégorie supprimée'); load(); }
    catch (e: any) { push(e.message, 'warn'); }
  };
  const pickCatPhoto = (c: { id: string; name: string }) => { catTargetRef.current = c; catFileRef.current?.click(); };
  const onCatFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const target = catTargetRef.current;
    if (!file || !target) return;
    try {
      const dataUrl = await fileToResizedDataURL(file, 240, 0.8);
      await api.updateCategory(target.id, { name: target.name, imageUrl: dataUrl });
      push('📷 Photo de catégorie ajoutée');
      load();
    } catch {
      push('Impossible de charger cette photo', 'warn');
    }
  };
  const removeCatPhoto = async (c: { id: string; name: string }) => {
    try { await api.updateCategory(c.id, { name: c.name, imageUrl: '' }); load(); }
    catch (e: any) { push(e.message, 'warn'); }
  };

  const onProdFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await fileToResizedDataURL(file, 360, 0.82);
      setForm((f) => (f ? { ...f, photoUrl: dataUrl } : f));
    } catch {
      push('Impossible de charger cette photo', 'warn');
    }
  };

  const del = async (p: ProductDto) => {
    if (!confirm(`Supprimer « ${p.name} » ?`)) return;
    try {
      await api.deleteProduct(p.id);
      push('🗑 Plat supprimé');
      load();
    } catch (e: any) {
      push(e.message, 'warn');
    }
  };

  if (importing) return <MenuImport onBack={() => setImporting(false)} onDone={load} />;

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 14px', flexWrap: 'wrap' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>🍛 Gestion de la carte</div>
        <button className="cf-reprint" style={{ width: 'auto', padding: '11px 14px' }} onClick={() => setImporting(true)}>📥 Importer Excel</button>
        <button className="cf-reprint" style={{ width: 'auto', padding: '11px 14px' }} onClick={() => setShowCats((v) => !v)}>🗂️ Catégories</button>
        <button className="btn-gold" style={{ fontSize: '1rem', padding: '11px 16px' }} onClick={openNew}>＋ Plat</button>
      </div>

      {showCats && (
        <div className="cc" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.2rem', flex: 1 }}>🗂️ Catégories</div>
            <button className="btn-gold" style={{ fontSize: '.9rem', padding: '8px 12px' }} onClick={addCat}>＋ Catégorie</button>
          </div>
          <input ref={catFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onCatFile} />
          {cats.map((c) => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--line)' }}>
              {c.imageUrl ? (
                <img src={c.imageUrl} title="Changer la photo" onClick={() => pickCatPhoto(c)}
                  style={{ width: 38, height: 38, borderRadius: 9, objectFit: 'cover', cursor: 'pointer', flexShrink: 0 }} />
              ) : (
                <button className="cr-btn" title="Ajouter une photo" onClick={() => pickCatPhoto(c)}>📷</button>
              )}
              <span style={{ fontSize: '1.3rem' }}>{c.emoji}</span>
              <span style={{ flex: 1, fontWeight: 800 }}>{c.name}</span>
              {c.imageUrl && <button className="cr-btn" title="Retirer la photo" onClick={() => removeCatPhoto(c)}>🚫</button>}
              <button className="cr-btn" onClick={() => renameCat(c)}>✏️</button>
              <button className="cr-btn del" onClick={() => delCat(c)}>🗑</button>
            </div>
          ))}
        </div>
      )}

      {menu.map((c) => (
        <div key={c.id} className="cc" style={{ marginBottom: 14 }}>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.2rem', color: 'var(--ink)', marginBottom: 8 }}>
            {c.emoji} {c.name} <span style={{ color: 'var(--ink-faint)', fontSize: '.8rem' }}>({c.products.length})</span>
          </div>
          {c.products.map((p) => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--line)' }}>
              <span style={{ fontSize: '1.4rem' }}>{p.emoji}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, opacity: p.available ? 1 : 0.4 }}>{p.name}</div>
                <div style={{ fontSize: '.74rem', color: 'var(--gold)', fontWeight: 800 }}>{euro(p.priceCents)}</div>
                {p.stock?.trackStock && (
                  <div style={{ fontSize: '.7rem', fontWeight: 700, color: p.stock.quantity <= 0 ? 'var(--cuisine)' : p.stock.quantity <= p.stock.threshold ? 'var(--orange)' : 'var(--ink-faint)' }}>
                    📦 {p.stock.quantity <= 0 ? 'Rupture' : `Stock ${p.stock.quantity}${p.stock.quantity <= p.stock.threshold ? ' · bas' : ''}`}
                  </div>
                )}
              </div>
              <button className="linkbtn" style={{ color: p.available ? 'var(--green)' : 'var(--ink-faint)' }} onClick={() => toggleAvail(p)}>
                {p.available ? '● Dispo' : '○ Masqué'}
              </button>
              <button className="cr-btn" title="Options & suppléments" onClick={() => setOptionsFor(p)}>⚙️</button>
              <button className="cr-btn" onClick={() => openEdit(p)}>✏️</button>
              <button className="cr-btn del" onClick={() => del(p)}>🗑</button>
            </div>
          ))}
          {c.products.length === 0 && <div className="no-data" style={{ padding: 12 }}>Aucun plat</div>}
        </div>
      ))}

      {/* ── Formulaire ajout / édition ── */}
      {form && (
        <div className="al-overlay" onClick={() => setForm(null)}>
          <div className="al-sheet" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '94dvh' }}>
            <div className="al-grip" />
            <div className="al-head">
              <div className="ic">🍛</div>
              <div><div className="ti">{form.id ? 'Modifier le plat' : 'Nouveau plat'}</div></div>
              <button className="al-close" onClick={() => setForm(null)}>✕</button>
            </div>
            <div className="al-grid" style={{ display: 'block' }}>
              <Field label="Nom du plat (FR)">
                <input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="🇬🇧 Nom du plat (EN) — optionnel">
                <input className="inp" placeholder="ex. Green Curry Chicken" value={form.nameEn} onChange={(e) => setForm({ ...form, nameEn: e.target.value })} />
              </Field>
              <div style={{ display: 'flex', gap: 10 }}>
                <Field label="Catégorie" style={{ flex: 1 }}>
                  <select className="inp" value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })}>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </Field>
                <Field label="Emoji" style={{ width: 80 }}>
                  <input className="inp" value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} />
                </Field>
              </div>
              <Field label="Photo du plat (optionnel)">
                <input ref={prodFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onProdFile} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {form.photoUrl ? (
                    <img src={form.photoUrl} style={{ width: 60, height: 60, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 60, height: 60, borderRadius: 10, background: 'var(--gold-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.7rem', flexShrink: 0 }}>
                      {form.emoji || '🍽️'}
                    </div>
                  )}
                  <button className="cf-reprint" style={{ width: 'auto', padding: '10px 14px' }} onClick={() => prodFileRef.current?.click()}>
                    📷 {form.photoUrl ? 'Changer la photo' : 'Prendre une photo'}
                  </button>
                  {form.photoUrl && (
                    <button className="linkbtn" style={{ color: 'var(--cuisine)' }} onClick={() => setForm({ ...form, photoUrl: '' })}>Retirer</button>
                  )}
                </div>
              </Field>
              <Field label="Prix (€)">
                <input className="inp" inputMode="decimal" placeholder="12.90" value={form.priceEuro} onChange={(e) => setForm({ ...form, priceEuro: e.target.value })} />
              </Field>
              <Field label="Description (FR)">
                <input className="inp" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
              <Field label="🇬🇧 Description (EN) — optionnel">
                <input className="inp" value={form.descriptionEn} onChange={(e) => setForm({ ...form, descriptionEn: e.target.value })} />
              </Field>
              <Field label="Badges (séparés par virgule)">
                <input className="inp" placeholder="Signature, Végé" value={form.badges} onChange={(e) => setForm({ ...form, badges: e.target.value })} />
              </Field>
              <Field label="Allergènes">
                <div className="pd-chips">
                  {ALLERGENS.map((a) => (
                    <div key={a} className={`pd-chip${form.allergens.includes(a) ? ' on' : ''}`} onClick={() => toggleAllergen(a)}>{a}</div>
                  ))}
                </div>
              </Field>
              <div className="al-toggle" style={{ marginTop: 12 }}>
                <span className="an">Disponible à la vente</span>
                <div className={`al-sw${form.available ? ' on' : ''}`} onClick={() => setForm({ ...form, available: !form.available })} />
              </div>
              <div className="al-toggle" style={{ marginTop: 10 }}>
                <span className="ai">📦</span>
                <span className="an">Suivi du stock (masque en rupture)</span>
                <div className={`al-sw${form.trackStock ? ' on' : ''}`} onClick={() => setForm({ ...form, trackStock: !form.trackStock })} />
              </div>
              {form.trackStock && (
                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <Field label="Quantité en stock" style={{ flex: 1 }}>
                    <input className="inp" inputMode="numeric" value={form.stockQty} onChange={(e) => setForm({ ...form, stockQty: e.target.value })} />
                  </Field>
                  <Field label="Seuil d'alerte" style={{ flex: 1 }}>
                    <input className="inp" inputMode="numeric" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} />
                  </Field>
                </div>
              )}
            </div>
            <button className="al-go" disabled={saving} onClick={save}>💾 ENREGISTRER</button>
          </div>
        </div>
      )}

      {optionsFor && (
        <OptionsEditor
          productId={optionsFor.id}
          productName={optionsFor.name}
          onClose={() => { setOptionsFor(null); load(); }}
        />
      )}
    </div>
  );
}

function Field({ label, children, style }: { label: string; children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <div style={{ fontSize: '.7rem', fontWeight: 800, color: 'var(--ink-dim)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>{label}</div>
      {children}
    </div>
  );
}
